import { z } from "zod";

import { MONEY_INPUT_PATTERN, normalizeDecimal, PERCENTAGE_INPUT_PATTERN } from "@/shared/utils/decimal";

import { PaymentFeeKind, type PaymentFee, type PaymentFeeTier } from "../../types/paymentFees.types";
import { isZeroDecimal, tierMemberToInput } from "../../utils/feeMath";

export interface PriceFormMessages {
  money: string;
  percentage: string;
  tierThreshold: string;
  tierThresholdUnique: string;
  tierFixed: string;
  tierPercentage: string;
}

export interface FeeFormMessages extends PriceFormMessages {
  corridorRequired: string;
  validUntil: string;
  validFromRequired: string;
  reasonRequired: string;
  ackRequired: string;
}

const tierRow = z.object({
  threshold: z.string(),
  fixed: z.string(),
  percentage: z.string(),
});

/**
 * Money and percentages stay strings checked against the backend's own patterns — a `z.number()` would round
 * an 8-decimal amount through a double.
 */
export function priceShape(messages: PriceFormMessages) {
  const money = z.string().refine((value) => value === "" || MONEY_INPUT_PATTERN.test(value), messages.money);
  const percentage = z
    .string()
    .refine((value) => value === "" || PERCENTAGE_INPUT_PATTERN.test(value), messages.percentage);
  return {
    percentage,
    fixed: money,
    minFee: money,
    maxFee: money,
    useTiers: z.boolean(),
    tiers: z.array(tierRow),
  };
}

type PriceValues = { useTiers: boolean; tiers: z.input<typeof tierRow>[] };

/** Stricter than the API on purpose: it caps range percentages at 100 and refuses two ranges starting at once. */
export function refinePrice(values: PriceValues, ctx: z.RefinementCtx, messages: PriceFormMessages): void {
  if (!values.useTiers) return;

  const thresholds: string[] = [];
  values.tiers.forEach((row, index) => {
    if (!MONEY_INPUT_PATTERN.test(row.threshold)) {
      ctx.addIssue({ code: "custom", path: ["tiers", index, "threshold"], message: messages.tierThreshold });
    } else {
      thresholds.push(normalizeDecimal(row.threshold));
    }
    if (row.fixed !== "" && !MONEY_INPUT_PATTERN.test(row.fixed)) {
      ctx.addIssue({ code: "custom", path: ["tiers", index, "fixed"], message: messages.tierFixed });
    }
    if (row.percentage !== "" && !PERCENTAGE_INPUT_PATTERN.test(row.percentage)) {
      ctx.addIssue({
        code: "custom",
        path: ["tiers", index, "percentage"],
        message: messages.tierPercentage,
      });
    }
  });

  if (new Set(thresholds).size !== thresholds.length) {
    ctx.addIssue({ code: "custom", path: ["tiers"], message: messages.tierThresholdUnique });
  }
}

export function buildFeeFormSchema(messages: FeeFormMessages) {
  return z
    .object({
      feeKind: z.enum([PaymentFeeKind.COLOCA_COMMISSION, PaymentFeeKind.PROVIDER_COST]),
      corridorId: z.string(),
      anyCountry: z.boolean(),
      anyRail: z.boolean(),
      anyCurrency: z.boolean(),
      anyDirection: z.boolean(),
      anyRoute: z.boolean(),
      ...priceShape(messages),
      effectiveNow: z.boolean(),
      validFromDate: z.string(),
      validFromTime: z.string(),
      validUntilDate: z.string(),
      validUntilTime: z.string(),
      reason: z.string().max(500),
      /** Set when the dialog reuses an existing rule's scope — scope validation is then skipped. */
      scopeLocked: z.boolean(),
      /** A general commission reaches every organization without a special price: it needs both. */
      requiresAck: z.boolean(),
      acknowledged: z.boolean(),
    })
    .superRefine((values, ctx) => {
      const everyDimensionAny =
        values.anyCountry && values.anyRail && values.anyCurrency && values.anyDirection;
      if (!values.scopeLocked && !values.corridorId && !everyDimensionAny) {
        ctx.addIssue({ code: "custom", path: ["corridorId"], message: messages.corridorRequired });
      }

      if (!values.effectiveNow && !values.validFromDate) {
        ctx.addIssue({ code: "custom", path: ["validFromDate"], message: messages.validFromRequired });
      }

      if (values.validUntilDate) {
        const until = Date.parse(`${values.validUntilDate}T${values.validUntilTime || "00:00"}`);
        const from = values.effectiveNow
          ? Date.now()
          : Date.parse(`${values.validFromDate}T${values.validFromTime || "00:00"}`);
        if (!Number.isNaN(until) && !Number.isNaN(from) && until <= from) {
          ctx.addIssue({ code: "custom", path: ["validUntilDate"], message: messages.validUntil });
        }
      }

      if (values.requiresAck) {
        if (!values.reason.trim()) {
          ctx.addIssue({ code: "custom", path: ["reason"], message: messages.reasonRequired });
        }
        if (!values.acknowledged) {
          ctx.addIssue({ code: "custom", path: ["acknowledged"], message: messages.ackRequired });
        }
      }

      refinePrice(values, ctx, messages);
    });
}

export type FeeFormValues = z.input<ReturnType<typeof buildFeeFormSchema>>;

export const PRICE_FORM_DEFAULTS = {
  percentage: "",
  fixed: "",
  minFee: "",
  maxFee: "",
  useTiers: false,
  tiers: [] as FeeFormValues["tiers"],
};

export const FEE_FORM_DEFAULTS: FeeFormValues = {
  feeKind: PaymentFeeKind.COLOCA_COMMISSION,
  corridorId: "",
  anyCountry: false,
  anyRail: false,
  anyCurrency: false,
  anyDirection: false,
  anyRoute: false,
  ...PRICE_FORM_DEFAULTS,
  effectiveNow: true,
  validFromDate: "",
  validFromTime: "09:00",
  validUntilDate: "",
  validUntilTime: "",
  reason: "",
  scopeLocked: false,
  requiresAck: false,
  acknowledged: false,
};

/** An existing row's price as form values. Zero terms seed as empty so the placeholder reads "0". */
export function seedPrice(source: PaymentFee | null): typeof PRICE_FORM_DEFAULTS {
  if (!source) return { ...PRICE_FORM_DEFAULTS, tiers: [] };
  const orEmpty = (value: string) => (isZeroDecimal(value) ? "" : value);
  return {
    percentage: orEmpty(source.percentage),
    fixed: orEmpty(source.fixed),
    minFee: orEmpty(source.min_fee),
    maxFee: source.max_fee ?? "",
    useTiers: !!(source.tiers && source.tiers.length > 0),
    // `tierMemberToInput`, not `String`: a stored 1e-8 would seed as "1e-8", which the form rejects.
    tiers: (source.tiers ?? []).map((tier) => ({
      threshold: tierMemberToInput(tier.threshold),
      fixed: tierMemberToInput(tier.fixed),
      percentage: tierMemberToInput(tier.percentage),
    })),
  };
}

/** Form rows → the API's numeric tier shape, sorted ascending: `tiers` is the one JSON-number field. */
export function toTierPayload(rows: FeeFormValues["tiers"]): PaymentFeeTier[] | null {
  const tiers = rows
    .filter((row) => row.threshold !== "")
    .map((row) => ({
      threshold: Number(row.threshold) || 0,
      fixed: Number(row.fixed) || 0,
      percentage: Number(row.percentage) || 0,
    }))
    .sort((a, b) => a.threshold - b.threshold);
  return tiers.length > 0 ? tiers : null;
}

/** The price part of a create payload: empty terms are left out and default to 0 server-side. */
export function pricePayload(values: typeof PRICE_FORM_DEFAULTS) {
  const tiers = values.useTiers ? toTierPayload(values.tiers) : null;
  return {
    ...(values.fixed ? { fixed: values.fixed } : {}),
    ...(values.percentage ? { percentage: values.percentage } : {}),
    ...(values.minFee ? { min_fee: values.minFee } : {}),
    ...(values.maxFee ? { max_fee: values.maxFee } : {}),
    ...(tiers ? { tiers } : {}),
  };
}

/** The candidate row the previews compute on, from live form values. */
export function candidatePrice(values: typeof PRICE_FORM_DEFAULTS) {
  return {
    fixed: values.fixed || "0",
    percentage: values.percentage || "0",
    min_fee: values.minFee || "0",
    max_fee: values.maxFee || null,
    tiers: values.useTiers ? toTierPayload(values.tiers) : null,
  };
}
